'use client';

import { useSelector } from '@xstate/react';
import { FeatureCheckoutFlowActor } from './checkoutFlow';
import { Loading } from '../common/Screens/Loading';
import { CoinsBuyingScreenWrapper } from '../BuyingCoins/Screens/main';
import { ChooseMethod } from '../common/Screens/ChooseMethod';
import { CardComPayment } from '../common/Screens/CardComPayment';
import { BankPayment } from '../common/Screens/BankPayment';
import { Error } from '../common/Screens/Error';
import { runtimeConfig } from '@/config/runtime-config';
import { getFeaturePriceBreakdown } from '@/utils/feature-price';

type Props = {
  actor: FeatureCheckoutFlowActor;
  onClose: () => void;
  onBackToSummary: () => void;
};

export function FeatureCheckout({ actor, onClose, onBackToSummary }: Props) {
  const checkoutState = useSelector(actor, (s) => s);
  const checkoutSend = actor.send;
  const selectedFeature = checkoutState.context.dict[checkoutState.context.selected[0]];
  const currencySign = selectedFeature ? getFeaturePriceBreakdown(selectedFeature).currencySign : '₪';

  if (checkoutState.matches('purchaseCoins')) {
    const childActor = (checkoutState.children as any)?.['coinPurchaseFlow'];
    if (!childActor) {
      return <Loading onClose={onClose} />;
    }
    return (
      <CoinsBuyingScreenWrapper
        onBack={() => checkoutSend({ type: 'PREV_PAGE' })}
        actor={childActor}
        onClose={onClose}
      />
    );
  }

  if (checkoutState.matches('chooseMethod')) {
    return (
      <ChooseMethod
        onClose={onClose}
        fiatAmountToPay={checkoutState.context.fiatCost}
        coinsAmountToPay={checkoutState.context.coinsCost}
        telegramStarsAmount={runtimeConfig.isTelegramMiniApp ? checkoutState.context.telegramStarsAmount : undefined}
        showCoinsAlternative={true}
        availableMethods={checkoutState.context.payments}
        onBack={onBackToSummary}
        balance={checkoutState.context.coinsBalance}
        currencySign={currencySign}
        onSelect={(id: string) => checkoutSend({ type: 'SELECT_METHOD', paymentSystem: id })}
      />
    );
  }

  if (checkoutState.matches('cardComDetails')) {
    if (checkoutState.context.partsLoading) {
      return <Loading onClose={onClose} />;
    }
    const cardComData = checkoutState.context.payments.find((val) => val.paymentSystem === 'CARD_COM');
    return (
      <CardComPayment
        name={cardComData?.name || ''}
        amountToPay={checkoutState.context.fiatCost}
        tokens={cardComData?.tokens || []}
        parts={checkoutState.context.parts || []}
        onBack={() => checkoutSend({ type: 'PREV_PAGE' })}
        onGoPay={(partsAmount: number, payerName: string, token?: string) => {
          checkoutSend({
            type: 'GO_PAY_CARD_COM',
            parts: partsAmount,
            payerName,
            token,
          });
        }}
        onClose={onClose}
      />
    );
  }

  if (checkoutState.matches('manualTransfer')) {
    return (
      <BankPayment
        onBack={() => checkoutSend({ type: 'PREV_PAGE' })}
        totalSum={String(checkoutState.context.fiatCost) || ''}
        currencySign={currencySign}
        onClose={onClose}
      />
    );
  }

  if (checkoutState.matches('error')) {
    return <Error onClose={onClose} onGoBackSelect={() => checkoutSend({ type: 'BACK_TO_SELECTION' })} />;
  }

  if (checkoutState.matches('processingPayment')) {
    return <Loading onClose={onClose} />;
  }

  return <Loading onClose={onClose} />;
}
